/**
 * Serviço de API Base
 * Configuração centralizada para chamadas ao backend
 */

import { auth } from "@services/firebase";
import { signInWithCustomToken } from "firebase/auth";
import storage from "@services/storage";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api/v1";

/**
 * Configuração padrão para requisições
 */
const defaultConfig = {
  headers: {
    "Content-Type": "application/json",
  },
};

/**
 * Obtém o token de autenticação do storage
 */
const getAuthToken = () => {
  return storage.getItem("auth_token");
};

/**
 * Adiciona headers de autenticação se disponível
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  if (token) {
    return {
      ...defaultConfig.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return defaultConfig.headers;
};

let isRefreshing = false;
let refreshPromise = null;
const inFlightGetRequests = new Map();

/**
 * Tenta renovar o token
 * @param {string} expiredAccessToken - O token de acesso que acabou de expirar
 */
const tryRefreshToken = async (expiredAccessToken) => {
  // Se já houver um refresh em andamento, retorna a promessa existente
  if (isRefreshing) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    const loginMethod = storage.getItem("login_method");
    if (!loginMethod) {
      console.error("Login method not found, cannot refresh token.");
      isRefreshing = false;
      refreshPromise = null;
      return false;
    }

    let refreshEndpoint = "";
    let requestBody = {};

    if (loginMethod === "google") {
      refreshEndpoint = `${API_BASE_URL}/auth/google/refresh`;
      requestBody = { refresh_token: expiredAccessToken };
    } else if (loginMethod === "credentials") {
      refreshEndpoint = `${API_BASE_URL}/auth/refresh`;
      requestBody = { refresh_token: expiredAccessToken };
    } else {
      console.error("Unknown login method:", loginMethod);
      isRefreshing = false;
      refreshPromise = null;
      return false;
    }

    try {
      const response = await fetch(refreshEndpoint, {
        method: "POST",
        headers: defaultConfig.headers,
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.access_token) {
          storage.setItem("auth_token", data.data.access_token);

          if (
            data.data.firebase_custom_token &&
            data.data.firebase_custom_token.length > 0
          ) {
            try {
              await signInWithCustomToken(
                auth,
                data.data.firebase_custom_token,
              );
              console.log(
                "Firebase Auth re-authenticated with custom token after API token refresh.",
              );
            } catch (fbErr) {
              console.error(
                "Error re-authenticating Firebase Auth after API token refresh:",
                fbErr,
              );
            }
          }

          isRefreshing = false;
          refreshPromise = null;
          return true;
        }
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }

    // Limpa tokens inválidos e dados do usuário se o refresh falhar
    storage.removeItem("auth_token");
    storage.removeItem("refresh_token");
    storage.removeItem("user");
    storage.removeItem("login_method");

    isRefreshing = false;
    refreshPromise = null;
    return false;
  })();

  return refreshPromise;
};

/**
 * Tratamento de erros da API e extração de dados
 */
const handleResponse = async (response, retryFn = null) => {
  const jsonResponse = await response.json().catch(() => ({})); // Always try to parse JSON

  if (!response.ok || !jsonResponse.success) {
    // Check both HTTP status and API's 'success' flag
    let errMessage = jsonResponse.message || jsonResponse.detail || jsonResponse.error;

    if (errMessage && typeof errMessage === "object") {
      if (Array.isArray(errMessage)) {
        errMessage = errMessage.map((e) => e.msg || e.message || JSON.stringify(e)).join(", ");
      } else {
        errMessage = errMessage.message || errMessage.detail || errMessage.error || JSON.stringify(errMessage);
      }
    }

    if (!errMessage || errMessage === "Erro desconhecido") {
      if (response.status === 429) {
        errMessage = "Muitas tentativas em pouco tempo. Por favor, aguarde alguns instantes e tente novamente.";
      } else if (response.status === 404) {
        errMessage = "Recurso não encontrado.";
      } else if (response.status === 403) {
        errMessage = "Você não tem permissão para realizar esta ação.";
      } else if (response.status === 500) {
        errMessage = "Erro interno do servidor. Tente novamente mais tarde.";
      } else {
        errMessage = "Erro inesperado. Entre em contato com nosso suporte.";
      }
    } else if (typeof errMessage === "string" && errMessage.toLowerCase().includes("rate limit exceeded")) {
      errMessage = "Muitas tentativas em pouco tempo. Por favor, aguarde alguns instantes e tente novamente.";
    }

    // Se token expirou com 401, tenta refresh apenas se for rota autenticada e não erro de credencial
    if (response.status === 401) {
      const isAuthCredentialEndpoint =
        response.url &&
        (response.url.includes("/auth/login") ||
          response.url.includes("/auth/reset-password") ||
          response.url.includes("/auth/refresh"));

      const isPasswordError =
        typeof errMessage === "string" &&
        errMessage.toLowerCase().includes("senha");

      if (!isAuthCredentialEndpoint && !isPasswordError) {
        if (retryFn) {
          const expiredAccessToken = getAuthToken();
          if (expiredAccessToken) {
            const refreshed = await tryRefreshToken(expiredAccessToken);
            if (refreshed) {
              // Retry original request
              return retryFn();
            }
          }
        }
        // Dispara evento de logout global para limpar credenciais e redirecionar para /login
        window.dispatchEvent(new CustomEvent("auth:logout"));
      }
    }

    const err = new Error(errMessage);
    err.status = response.status;
    err.data = jsonResponse.data; // Include any data returned with the error
    throw err;
  }

  // If successful, return the data field
  return jsonResponse.data;
};

/**
 * Métodos HTTP
 */
export const api = {
  /**
   * GET request
   */
  get: async (endpoint, params = {}, options = {}) => {
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    const makeRequest = async () => {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      return handleResponse(response, makeRequest);
    };

    if (options.dedupe === false) {
      return makeRequest();
    }

    const token = getAuthToken() || "anonymous";
    const cacheKey = `${token}:${url.toString()}`;

    if (inFlightGetRequests.has(cacheKey)) {
      return inFlightGetRequests.get(cacheKey);
    }

    const requestPromise = (async () => {
      try {
        return await makeRequest();
      } finally {
        inFlightGetRequests.delete(cacheKey);
      }
    })();

    inFlightGetRequests.set(cacheKey, requestPromise);
    return requestPromise;
  },

  /**
   * POST request
   */
  post: async (endpoint, data = {}) => {
    const makeRequest = async () => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response, makeRequest);
    };

    return makeRequest();
  },

  /**
   * PUT request
   */
  put: async (endpoint, data = {}) => {
    const makeRequest = async () => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response, makeRequest);
    };

    return makeRequest();
  },

  /**
   * PATCH request
   */
  patch: async (endpoint, data = {}) => {
    const makeRequest = async () => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response, makeRequest);
    };

    return makeRequest();
  },

  /**
   * DELETE request
   */
  delete: async (endpoint) => {
    const makeRequest = async () => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      return handleResponse(response, makeRequest);
    };

    return makeRequest();
  },

  /**
   * Upload de arquivo
   */
  upload: async (endpoint, formData) => {
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const makeRequest = async () => {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers,
        body: formData,
      });
      return handleResponse(response, makeRequest);
    };

    return makeRequest();
  },

  checkOrCreateUser: async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/check-or-create`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      const jsonResponse = await response.json().catch(() => ({}));

      if (!response.ok || jsonResponse.success === false) {
        const errMessage = jsonResponse.message || "Erro ao verificar usuário.";
        const err = new Error(errMessage);
        err.status = response.status;
        err.data = jsonResponse.data;
        throw err;
      }

      return {
        success: true,
        data: jsonResponse.data,
        message: jsonResponse.message,
      };
    } catch (error) {
      console.error("API: Error in checkOrCreateUser:", error);
      return {
        success: false,
        status: error.status || 500,
        message: error.message,
        data: error.data,
      };
    }
  },
};

export default api;
